
/* =============================================================================
 * lib/actions/auth.actions.ts - PRODUCTION READY WITH P0 FIXES
 * -----------------------------------------------------------------------------
 * CRITICAL FIXES APPLIED:
 * ✅ Update validateSession() to return tri-state: AUTHENTICATED, UNAUTHENTICATED, UNKNOWN
 * ✅ UNKNOWN never redirects to /login
 * ✅ refreshSession() only called when explicitly needed (401)
 * ✅ No random redirects on timeouts
 * =============================================================================
 */

import { cache } from 'react';

// Dummy implementations for static export compatibility
// revalidatePath and revalidateTag are not available in client-side static builds
const revalidatePath = (path: string, type?: 'page' | 'layout') => {};
const revalidateTag = (tag: string, type?: 'page' | 'layout') => {};


import {
  serverApiClient, 
  ApiSuccessResponse,
  ApiErrorResponse,
  StandardizedError,
} from '@/lib/api/serverApiClient.server';

import { UserRole } from '../types/auth.types';
import { encryptionService } from '../server-utils/encryption';
import { CookieManager } from '../server-utils/cookieManager';

/* =============================================================================
 * INTERNAL TYPES
 * =============================================================================
 */

type ValidationError = { field: string; message: string; code?: string };

type ApiErrorMeta = {
  httpStatus?: number;
  requestId?: string;
  path?: string;
  method?: string;
  [k: string]: unknown;
};

type ApiErrorWithMeta = ApiErrorResponse & { meta?: ApiErrorMeta };

interface SessionMetadata {
  id: string;
  userId: string;
  createdAt: number;
  lastActive: number;
  deviceId: string;
  deviceName: string;
  ipAddress?: string;
  userAgent?: string;
  location?: string;
  expiresAt: number;
  isValid: boolean;
  role?: UserRole;
}

interface AuthCookies {
  access_token?: string;
  refresh_token?: string;
  user_id?: string;
  session_id?: string;
  device_id?: string;
  device_name?: string;
  remember_me?: string;
  auth_state?: string;
  role?: UserRole;
}

interface UserPreferences {
  theme: string;
  language: string;
  notifications: boolean;
  push_notifications: boolean;
  email_notifications: boolean;
}

interface NotificationSettings {
  sms: { security: boolean; order_updates: boolean };
  push: { promotions: boolean; order_updates: boolean };
  email: { security: boolean; marketing: boolean; transactional: boolean };
}

interface UserData {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  phone: string;
  user_type: 'customer' | 'owner' | 'waiter' | 'admin';
  avatar: string | null;
  status: string;
  is_vendor_owner: boolean;
  is_vendor_staff: boolean;
  is_indeadmin: boolean;
  is_customer: boolean;
  vendor_count: number;
  can_create_vendor: boolean;
  vendor_limit: number;
  email_verified: boolean;
  phone_verified: boolean;
  is_active: boolean;
  is_locked: boolean;
  lock_remaining_minutes: number;
  two_factor_enabled: boolean;
  requires_password_change: boolean;
  created_at: string;
  permissions: string[];
  preferences: UserPreferences;
  notification_settings: NotificationSettings;
  verification_status: string;
  locale: string;
  timezone: string;
  currency: string;
  login_attempts: number;
  max_login_attempts: number;
  remaining_attempts: number;
}

interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
  access_token_expires_in: number;
  refresh_token_expires_in: number;
  access_token_expires_at: string;
  refresh_token_expires_at: string;
  device_id: string;
  device_name: string;
  role: UserRole;
}

interface ServerAuthResponse {
  status:
    | 'success'
    | 'error'
    | 'requires_2fa'
    | 'requires_verification'
    | 'password_change'
    | 'authenticated'
    | 'unauthenticated'
    | 'unknown'; // ✅ NEW: Added unknown status
  message: string;
  data?: {
    user?: UserData;
    auth?: AuthTokens;
    permissions?: string[];
    redirectTo?: string;
    requiresEmailVerification?: boolean;
    sessionId?: string;
    deviceId?: string;
    sessions?: SessionMetadata[];
    [key: string]: any;
  };
  errors?: ValidationError[];
  timestamp?: string;
  sessionExpiresAt?: number;
}

// ✅ UPDATED: Tri-state token validation response
interface TokenValidationResponse {
  status: 'AUTHENTICATED' | 'UNAUTHENTICATED' | 'UNKNOWN';
  userId?: string;
  user?: UserData;
  accessToken?: string;
  sessionId?: string;
  expiresIn?: number;
  role?: UserRole;
  error?: {
    code: string;
    message: string;
    isTransient?: boolean;
  };
}

// P0 FIX: Structured refresh result with proper error classification
interface RefreshTokenResult {
  ok: boolean;
  logoutRequired: boolean;
  transient: boolean;
  reason: 'invalid_refresh' | 'timeout' | 'network' | 'server_error' | 'success' | 'no_session';
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  user?: UserData;
  error?: {
    code: string;
    message: string;
    httpStatus?: number;
  };
}

interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

interface RegisterCredentials {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  password_confirmation: string;
  phone: string;
  user_type: 'customer' | 'owner' | 'waiter' | 'admin';
  terms_accepted: boolean;
}

/* =============================================================================
 * CONFIG
 * =============================================================================
 */

const SECURITY_CONFIG = {
  ACCESS_TOKEN_MAX_AGE: 15 * 60, // 15 minutes
  REFRESH_TOKEN_MAX_AGE: 7 * 24 * 60 * 60, // 7 days
  REFRESH_TOKEN_MAX_AGE_REMEMBER: 30 * 24 * 60 * 60, // 30 days
  MAX_REFRESH_ATTEMPTS: 3,
  REFRESH_COOLDOWN_MS: 10_000,
  ACCESS_TOKEN_REFRESH_THRESHOLD: 5 * 60, // Refresh if token expires in 5 minutes
  MIN_REFRESH_INTERVAL: 30_000, // Don't refresh more than once every 30 seconds
} as const;

const ENV_CONFIG = {
  API_TIMEOUT:
    Number(process.env.NEXT_PUBLIC_API_TIMEOUT ?? '') > 0
      ? Number(process.env.NEXT_PUBLIC_API_TIMEOUT)
      : 30_000,
  API_BASE_URL:
    process.env.NEXT_PUBLIC_API_BASE_URL || 'http://127.0.0.1:8000/api/v1',
  LOG_SECURITY_EVENTS: process.env.NODE_ENV === 'production',
} as const;

const cookieManager = CookieManager;

/* =============================================================================
 * INTERNAL UTILITIES
 * =============================================================================
 */

function safeLower(s: unknown): string {
  return typeof s === 'string' ? s.toLowerCase() : String(s ?? '').toLowerCase();
}

function isObject(v: unknown): v is Record<string, any> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function isApiError(err: unknown): err is ApiErrorResponse {
  if (!isObject(err)) return false;
  return err.status === 'error' && typeof err.message === 'string' && 'data' in err;
}

function getHttpStatusFromError(err: unknown): number | undefined {
  if (!isObject(err)) return undefined;

  const anyErr = err as Record<string, any>;

  const metaStatus = anyErr?.meta?.httpStatus;
  if (typeof metaStatus === 'number') return metaStatus;

  const status = anyErr?.statusCode ?? anyErr?.status;
  if (typeof status === 'number') return status;

  const responseStatus = anyErr?.response?.status;
  if (typeof responseStatus === 'number') return responseStatus;

  const causeStatus = anyErr?.cause?.status ?? anyErr?.cause?.statusCode;
  if (typeof causeStatus === 'number') return causeStatus;

  return undefined;
}

// ✅ IMPROVED: Enhanced error classification for tri-state validation
function classifyError(error: unknown): {
  isUnauthorized: boolean;
  isForbidden: boolean;
  isTransient: boolean;
  isAuthExpired: boolean;
  category: 'auth' | 'network' | 'server' | 'timeout' | 'unknown';
  shouldRetry: boolean;
} {
  const httpStatus = getHttpStatusFromError(error);
  const isUnauthorized = httpStatus === 401 || httpStatus === 419;
  const isForbidden = httpStatus === 403;
  
  const isServerError = httpStatus !== undefined && httpStatus >= 500 && httpStatus < 600;
  
  const msg = safeLower(error instanceof Error ? error.message : String(error ?? ''));
  const isNetworkError = 
    msg.includes('network') || 
    msg.includes('econn') ||
    msg.includes('socket') ||
    msg.includes('failed to fetch');
  
  const isTimeout = 
    msg.includes('timeout') ||
    msg.includes('timed out') ||
    msg.includes('etimedout') ||
    msg.includes('econnaborted') ||
    msg.includes('abort');
  
  const isTransient = isServerError || isNetworkError || isTimeout;
  
  let category: 'auth' | 'network' | 'server' | 'timeout' | 'unknown' = 'unknown';
  if (isUnauthorized || isForbidden) category = 'auth';
  else if (isNetworkError) category = 'network';
  else if (isServerError) category = 'server';
  else if (isTimeout) category = 'timeout';
  
  // ✅ NEW: Differentiate between auth expired and transient network errors
  const isAuthExpired = isUnauthorized && !isTransient;
  
  // Should retry on transient errors
  const shouldRetry = isTransient;
  
  return {
    isUnauthorized,
    isForbidden,
    isTransient,
    isAuthExpired,
    category,
    shouldRetry,
  };
}

function toValidationErrors(errs: any): ValidationError[] {
  if (!errs) return [];
  if (Array.isArray(errs)) {
    return errs.map((e) => ({
      field: String(e?.field ?? 'general'),
      message: String(e?.message ?? 'Validation error'),
      code: e?.code ? String(e.code) : undefined,
    }));
  }
  if (isObject(errs)) {
    return Object.entries(errs).flatMap(([field, messages]) => {
      const arr = Array.isArray(messages) ? messages : [messages];
      return arr.map((m) => ({
        field,
        message: String(m),
        code: 'VALIDATION_ERROR',
      }));
    });
  }
  return [{ field: 'general', message: String(errs), code: 'VALIDATION_ERROR' }];
}

function normalizeAuthResponseFromApiSuccess<T extends Record<string, any>>(
  res: ApiSuccessResponse<T>,
  message: string
): ServerAuthResponse {
  return {
    status: 'success',
    message,
    data: (res.data ?? {}) as any,
    timestamp: res.timestamp,
  };
}

function handleApiError(error: unknown): ServerAuthResponse {
  const httpStatus = getHttpStatusFromError(error);
  const classification = classifyError(error);

  if (isApiError(error)) {
    const e = error as ApiErrorWithMeta;

    if (classification.isAuthExpired) {
      return {
        status: 'unauthenticated',
        message: e.message || 'Session expired',
        errors: toValidationErrors(e.errors),
        timestamp: e.timestamp,
      };
    }

    if (classification.isForbidden) {
      return {
        status: 'error',
        message: e.message || 'Access denied',
        errors: toValidationErrors(e.errors?.length ? e.errors : [{ field: 'auth', message: 'Forbidden', code: 'FORBIDDEN' }]),
        timestamp: e.timestamp,
      };
    }

    if (httpStatus === 422) {
      return {
        status: 'error',
        message: e.message || 'Validation failed',
        errors: toValidationErrors(e.errors),
        timestamp: e.timestamp,
      };
    }

    return {
      status: 'error',
      message: e.message || 'Request failed',
      errors: toValidationErrors(e.errors),
      timestamp: e.timestamp,
    };
  }

  const msg =
    error instanceof Error ? error.message : typeof error === 'string' ? error : String(error ?? 'Unknown error');

  // ✅ UPDATED: Return UNKNOWN status for transient errors instead of error
  if (classification.category === 'timeout') {
    return {
      status: 'unknown', // ✅ CHANGED: timeout → unknown (not error)
      message: 'Request timeout - please try again',
      errors: [{ field: 'timeout', message: 'Request took too long', code: 'TIMEOUT' }],
    };
  }

  if (classification.category === 'network') {
    return {
      status: 'unknown', // ✅ CHANGED: network → unknown (not error)
      message: 'Network connection issue - please check your connection',
      errors: [{ field: 'network', message: 'Network error', code: 'NETWORK_ERROR' }],
    };
  }

  if (classification.category === 'server') {
    return {
      status: 'unknown', // ✅ CHANGED: server → unknown (not error)
      message: 'Server temporarily unavailable - please try again later',
      errors: [{ field: 'server', message: 'Server error', code: 'SERVER_ERROR' }],
    };
  }

  if (classification.isAuthExpired) {
    return {
      status: 'unauthenticated',
      message: 'Session expired',
      errors: [{ field: 'auth', message: 'Unauthorized', code: 'UNAUTHORIZED' }],
    };
  }

  return {
    status: 'error',
    message: msg || 'Operation failed',
    errors: [{ field: 'general', message: 'Operation failed', code: 'UNKNOWN_ERROR' }],
  };
}

function validateEmail(email: string): boolean {
  const v = String(email ?? '').trim();
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(v);
}

function validatePassword(password: string): { isValid: boolean; errors: string[] } {
  const pw = String(password ?? '');
  const errors: string[] = [];

  if (pw.length < 8) errors.push('Minimum 8 characters');
  if (!/[A-Z]/.test(pw)) errors.push('At least one uppercase letter');
  if (!/[a-z]/.test(pw)) errors.push('At least one lowercase letter');
  if (!/\d/.test(pw)) errors.push('At least one number');
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pw)) {
    errors.push('At least one special character');
  }

  return { isValid: errors.length === 0, errors };
}

function validateRegistration(credentials: RegisterCredentials): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!credentials.first_name?.trim()) {
    errors.push({ field: 'first_name', message: 'First name required', code: 'REQUIRED' });
  }
  if (!credentials.last_name?.trim()) {
    errors.push({ field: 'last_name', message: 'Last name required', code: 'REQUIRED' });
  }
  if (!credentials.email?.trim()) {
    errors.push({ field: 'email', message: 'Email required', code: 'REQUIRED' });
  } else if (!validateEmail(credentials.email)) {
    errors.push({ field: 'email', message: 'Invalid email format', code: 'INVALID_EMAIL' });
  }

  const pw = validatePassword(credentials.password);
  if (!pw.isValid) {
    pw.errors.forEach((m) => errors.push({ field: 'password', message: m, code: 'VALIDATION_FAILED' }));
  }

  if (credentials.password !== credentials.password_confirmation) {
    errors.push({
      field: 'password_confirmation',
      message: 'Passwords must match',
      code: 'PASSWORD_MISMATCH',
    });
  }

  if (!credentials.phone?.trim()) {
    errors.push({ field: 'phone', message: 'Phone number required', code: 'REQUIRED' });
  }

  if (!credentials.terms_accepted) {
    errors.push({
      field: 'terms_accepted',
      message: 'Must accept terms and conditions',
      code: 'TERMS_REQUIRED',
    });
  }

  return errors;
}

function getDashboardPath(user: UserData): string {
  const dashboardPaths: Record<string, string> = {
    admin: '/dashboards/admin',
    owner: '/dashboards/vendor',
    waiter: '/dashboards/waiter',
    customer: '/dashboards/customer',
  };
  return dashboardPaths[user.user_type] || '/';
}

// Token refresh optimization - track last refresh time
let lastRefreshAttempt: { [sessionId: string]: number } = {};

function shouldAttemptRefresh(sessionId: string): boolean {
  const lastAttempt = lastRefreshAttempt[sessionId];
  if (!lastAttempt) return true;
  
  const now = Date.now();
  return now - lastAttempt >= SECURITY_CONFIG.MIN_REFRESH_INTERVAL;
}

function recordRefreshAttempt(sessionId: string): void {
  lastRefreshAttempt[sessionId] = Date.now();
  
  const cleanupTime = Date.now() - (5 * 60 * 1000);
  Object.keys(lastRefreshAttempt).forEach(key => {
    if (lastRefreshAttempt[key] < cleanupTime) {
      delete lastRefreshAttempt[key];
    }
  });
}

/* =============================================================================
 * DEDUPED /auth/me (INTERNAL)
 * =============================================================================
 */

type AuthMePayload = {
  user?: UserData;
  permissions?: string[];
};

const getCurrentUserRaw = async (): Promise<ApiSuccessResponse<AuthMePayload>> => {
  return serverApiClient.get<AuthMePayload>('/auth/me', { 
    timeout: ENV_CONFIG.API_TIMEOUT 
  });
};

const getCurrentUserCoalesced = cache(async () => getCurrentUserRaw());

/* =============================================================================
 * SERVER ACTIONS
 * =============================================================================
 */

/* -------------------------------- Cookies -------------------------------- */

export async function handleInvalidSession(): Promise<{ redirected: boolean }> {
  try {
    await cookieManager.clearAuthCookies();
    revalidatePath('/', 'layout');
    return { redirected: true };
  } catch (error) {
    console.error('Failed to handle invalid session:', error);
    return { redirected: false };
  }
}

export async function clearAuthCookiesAction(): Promise<{ success: boolean; message: string }> {
  try {
    await cookieManager.clearAuthCookies();
    revalidatePath('/', 'layout');
    return { success: true, message: 'Cookies cleared successfully' };
  } catch (error) {
    console.error('clearAuthCookiesAction failed:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to clear cookies',
    };
  }
}

/* ---------------------------- Session Validation --------------------------- */

// ✅ UPDATED: Tri-state validation function
export async function validateSession(): Promise<TokenValidationResponse> {
  try {
    const authCookies = (await cookieManager.getAuthCookies()) as AuthCookies;
    const token = authCookies.access_token;

    if (!token || !authCookies.user_id || !authCookies.session_id) {
      return {
        status: 'UNAUTHENTICATED',
        error: { code: 'MISSING_CREDENTIALS', message: 'Session cookies missing' },
      };
    }

    let userId: string;
    try {
      userId = encryptionService.decrypt(authCookies.user_id);
    } catch {
      return {
        status: 'UNAUTHENTICATED',
        error: { code: 'INVALID_SESSION', message: 'Failed to decrypt session data' },
      };
    }

    // ✅ FIX: Use meta config to indicate this is an auth check
    const response = await serverApiClient.get<AuthMePayload>('/auth/me', { 
      meta: { label: 'session-validation' },
      timeout: ENV_CONFIG.API_TIMEOUT 
    });
    
    const me = response.data?.user;

    if (!me) {
      return {
        status: 'UNAUTHENTICATED',
        error: { code: 'INVALID_SESSION', message: 'No user data returned' },
      };
    }

    return {
      status: 'AUTHENTICATED',
      userId,
      user: me,
      accessToken: token,
      sessionId: authCookies.session_id,
      expiresIn: SECURITY_CONFIG.ACCESS_TOKEN_MAX_AGE,
      role: me.user_type as UserRole,
    };
  } catch (error) {
    // ✅ FIX: Handle different error types with tri-state logic
    const classification = classifyError(error);
    
    if (classification.isAuthExpired) {
      // Clear auth expired - session is invalid
      return {
        status: 'UNAUTHENTICATED',
        error: { 
          code: 'TOKEN_EXPIRED', 
          message: 'Session expired',
          isTransient: false 
        },
      };
    }
    
    if (classification.isTransient) {
      // ✅ FIX: Timeout/network errors return UNKNOWN, not UNAUTHENTICATED
      return {
        status: 'UNKNOWN',
        error: { 
          code: classification.category.toUpperCase(), 
          message: classification.category === 'timeout' 
            ? 'Request timeout' 
            : classification.category === 'network'
            ? 'Network error'
            : 'Server temporarily unavailable',
          isTransient: true 
        },
      };
    }
    
    // Other non-transient errors
    return {
      status: 'UNKNOWN',
      error: { 
        code: 'VALIDATION_ERROR', 
        message: 'Unable to validate session',
        isTransient: false 
      },
    };
  }
}

/* ------------------------------- Token Refresh ----------------------------- */

export async function refreshSession(): Promise<RefreshTokenResult> {
  const c = (await cookieManager.getAuthCookies()) as AuthCookies;
  const sessionId = c.session_id;

  if (!sessionId) {
    return {
      ok: false,
      logoutRequired: true,
      transient: false,
      reason: 'no_session',
      error: { code: 'NO_SESSION', message: 'No active session found' },
    };
  }

  // Check rate limiting and cooldown
  if (!cookieManager.canAttemptRefresh(sessionId)) {
    return {
      ok: false,
      logoutRequired: true,
      transient: false,
      reason: 'invalid_refresh',
      error: { code: 'RATE_LIMITED', message: 'Too many refresh attempts. Please login again.' },
    };
  }

  // Optimize refresh calls - don't refresh too frequently
  if (!shouldAttemptRefresh(sessionId)) {
    return {
      ok: false,
      logoutRequired: false,
      transient: true,
      reason: 'network',
      error: { code: 'TOO_SOON', message: 'Refresh attempted too soon' },
    };
  }

  cookieManager.recordRefreshAttempt(sessionId);
  recordRefreshAttempt(sessionId);

  try {
    if (!c.refresh_token) {
      return {
        ok: false,
        logoutRequired: true,
        transient: false,
        reason: 'invalid_refresh',
        error: { code: 'NO_REFRESH_TOKEN', message: 'Refresh token missing' },
      };
    }

    let userId: string;
    try {
      userId = encryptionService.decrypt(c.user_id || '');
    } catch {
      return {
        ok: false,
        logoutRequired: true,
        transient: false,
        reason: 'invalid_refresh',
        error: { code: 'INVALID_SESSION', message: 'Invalid session data' },
      };
    }

    const response = await serverApiClient.post<{
      auth?: {
        access_token?: string;
        refresh_token?: string;
        access_token_expires_in?: number;
        refresh_token_expires_in?: number;
        device_id?: string;
        device_name?: string;
        token_type?: string;
      };
      user?: UserData;
      permissions?: string[];
    }>(
      '/auth/refresh-token',
      { refresh_token: c.refresh_token },
      {
        meta: { label: 'token-refresh' },
        timeout: ENV_CONFIG.API_TIMEOUT,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const auth = response.data?.auth;
    const user = response.data?.user;

    const accessToken = auth?.access_token?.trim();
    const refreshToken = auth?.refresh_token?.trim() || c.refresh_token;

    if (!accessToken) {
      return {
        ok: false,
        logoutRequired: true,
        transient: false,
        reason: 'invalid_refresh',
        error: { code: 'REFRESH_FAILED', message: 'Failed to refresh token' },
      };
    }

    const rememberMe = c.remember_me === 'true';
    const expiresIn =
      Number(auth?.access_token_expires_in ?? 0) || SECURITY_CONFIG.ACCESS_TOKEN_MAX_AGE;
    const role = (user?.user_type || c.role || 'customer') as UserRole;

    await cookieManager.setAuthCookies({
      userId,
      accessToken,
      refreshToken,
      sessionId,
      deviceId: c.device_id,
      deviceName: c.device_name,
      rememberMe,
      expiresIn,
      role,
    });

    cookieManager.resetRefreshAttempts(sessionId);

    revalidateTag('user-session', 'layout');
    revalidatePath('/', 'layout');

    return {
      ok: true,
      logoutRequired: false,
      transient: false,
      reason: 'success',
      accessToken,
      refreshToken,
      expiresIn,
      user,
    };
  } catch (error) {
    const classification = classifyError(error);
    const httpStatus = getHttpStatusFromError(error);

    // ✅ FIX: Only logout on 401/403, not on transient errors
    if (classification.isAuthExpired || classification.isForbidden) {
      await cookieManager.clearAuthCookies();
      
      return {
        ok: false,
        logoutRequired: true,
        transient: false,
        reason: 'invalid_refresh',
        error: {
          code: classification.isAuthExpired ? 'REFRESH_TOKEN_EXPIRED' : 'FORBIDDEN',
          message: classification.isAuthExpired 
            ? 'Session expired. Please login again.' 
            : 'Access denied. Please login again.',
          httpStatus,
        },
      };
    }

    // ✅ FIX: Handle transient errors without logout
    if (classification.isTransient) {
      console.warn('Transient refresh error (non-fatal):', {
        error: error instanceof Error ? error.message : String(error),
        category: classification.category,
        sessionId: sessionId?.substring(0, 8),
      });

      let reason: 'timeout' | 'network' | 'server_error' = 'network';
      if (classification.category === 'timeout') reason = 'timeout';
      else if (classification.category === 'server') reason = 'server_error';

      return {
        ok: false,
        logoutRequired: false,
        transient: true,
        reason,
        error: {
          code: classification.category.toUpperCase(),
          message: classification.category === 'timeout' 
            ? 'Refresh request timed out' 
            : classification.category === 'server'
            ? 'Server temporarily unavailable'
            : 'Network connection issue',
          httpStatus,
        },
      };
    }

    // For other errors, don't clear cookies - let user retry
    console.error('Refresh error (non-transient):', error);
    return {
      ok: false,
      logoutRequired: false,
      transient: false,
      reason: 'server_error',
      error: {
        code: 'REFRESH_ERROR',
        message: 'Failed to refresh session',
        httpStatus,
      },
    };
  }
}

// Optimized refresh wrapper - only refresh when necessary
export async function refreshIfNeeded(): Promise<RefreshTokenResult> {
  try {
    const authCookies = (await cookieManager.getAuthCookies()) as AuthCookies;
    
    // Check if we have the minimum required cookies
    if (!authCookies.access_token || !authCookies.refresh_token || !authCookies.user_id) {
      return {
        ok: false,
        logoutRequired: true,
        transient: false,
        reason: 'no_session',
        error: { code: 'INCOMPLETE_SESSION', message: 'Session incomplete' },
      };
    }

    // ✅ FIX: Only refresh if we have a clear indication it's needed (401/419)
    // The actual refresh decision is now handled by the API client
    
    // Check rate limiting first
    const sessionId = authCookies.session_id;
    if (sessionId && !cookieManager.canAttemptRefresh(sessionId)) {
      return {
        ok: false,
        logoutRequired: true,
        transient: false,
        reason: 'invalid_refresh',
        error: { code: 'RATE_LIMITED', message: 'Too many refresh attempts' },
      };
    }

    // Attempt refresh
    return await refreshSession();
  } catch (error) {
    console.error('Error in refreshIfNeeded:', error);
    return {
      ok: false,
      logoutRequired: false,
      transient: true,
      reason: 'network',
      error: { code: 'UNEXPECTED_ERROR', message: 'Unexpected error during refresh check' },
    };
  }
}

/* -------------------------- Auth Middleware Helper ------------------------- */

export async function withAuth(
  handler: (session: TokenValidationResponse) => Promise<any>,
  options?: {
    requireAuth?: boolean;
    autoRefresh?: boolean;
    roles?: UserRole[];
    redirectOnFail?: string;
  }
): Promise<any> {
  const config = {
    requireAuth: true,
    autoRefresh: true,
    roles: [] as UserRole[],
    redirectOnFail: '/login',
    ...options,
  };

  try {
    const session = await validateSession();

    // ✅ FIX: Handle tri-state responses properly
    switch (session.status) {
      case 'AUTHENTICATED':
        // Check roles if specified
        if (config.roles.length > 0 && session.role && !config.roles.includes(session.role)) {
          const err = new Error('Access denied');
          (err as any).statusCode = 403;
          throw err;
        }
        return handler(session);

      case 'UNAUTHENTICATED':
        // Only attempt refresh if autoRefresh is enabled
        if (config.requireAuth && config.autoRefresh) {
          const refreshResult = await refreshIfNeeded();
          
          if (refreshResult.ok && refreshResult.user) {
            // Refresh succeeded - retry validation
            const refreshedSession = await validateSession();
            if (refreshedSession.status === 'AUTHENTICATED') {
              // Check roles
              if (config.roles.length > 0 && refreshedSession.role && !config.roles.includes(refreshedSession.role)) {
                const err = new Error('Access denied');
                (err as any).statusCode = 403;
                throw err;
              }
              return handler(refreshedSession);
            }
          }
          
          if (refreshResult.logoutRequired) {
            // Auth failure - redirect to login
            const redirectError = new Error(
              `Authentication required. Redirecting to ${config.redirectOnFail}`
            );
            (redirectError as any).redirect = config.redirectOnFail;
            (redirectError as any).statusCode = 401;
            throw redirectError;
          }
        }
        
        // No refresh or refresh failed - redirect if auth required
        if (config.requireAuth) {
          const redirectError = new Error(
            `Authentication required. Redirecting to ${config.redirectOnFail}`
          );
          (redirectError as any).redirect = config.redirectOnFail;
          (redirectError as any).statusCode = 401;
          throw redirectError;
        }
        break;

      case 'UNKNOWN':
        // ✅ FIX: UNKNOWN status NEVER redirects to login
        if (config.requireAuth) {
          // For unknown status, throw service unavailable instead of redirecting
          const transientError = new Error(
            'Service temporarily unavailable. Please try again.'
          );
          (transientError as any).statusCode = 503;
          (transientError as any).transient = true;
          throw transientError;
        }
        // If auth not required, continue with handler
        break;
    }

    return handler(session);
  } catch (error: any) {
    if (error?.transient) {
      console.warn('Transient auth error (non-fatal):', {
        message: error?.message,
        statusCode: error?.statusCode,
      });
    } else {
      console.error('Auth middleware error:', {
        message: error?.message,
        redirect: error?.redirect,
        statusCode: error?.statusCode,
      });
    }
    
    throw error;
  }
}

/* -------------------------- Registration & Login --------------------------- */

export async function registerUser(credentials: RegisterCredentials): Promise<ServerAuthResponse> {
  try {
    const validationErrors = validateRegistration(credentials);
    if (validationErrors.length > 0) {
      return { status: 'error', message: 'Validation failed', errors: validationErrors };
    }

    const response = await serverApiClient.post<{
      user?: UserData;
      auth?: AuthTokens;
      permissions?: string[];
      redirectTo?: string;
      requiresEmailVerification?: boolean;
    }>(
      '/auth/register',
      { ...credentials, email: credentials.email.toLowerCase().trim() },
      { timeout: ENV_CONFIG.API_TIMEOUT }
    );

    const userData = response.data?.user;
    const authData = response.data?.auth;

    if (authData?.access_token && userData) {
      await cookieManager.setAuthCookies({
        userId: userData.id,
        accessToken: authData.access_token,
        refreshToken: authData.refresh_token,
        rememberMe: true,
        expiresIn: authData.access_token_expires_in || SECURITY_CONFIG.ACCESS_TOKEN_MAX_AGE,
        role: userData.user_type as UserRole,
      });

      revalidateTag('user-session', 'layout');
      revalidatePath('/', 'layout');

      return {
        status: 'success',
        message: 'Registration successful',
        data: { user: userData, auth: authData, redirectTo: getDashboardPath(userData) },
      };
    }

    return {
      status: 'success',
      message: 'Registration successful. Please check your email for verification.',
      data: {
        user: userData,
        requiresEmailVerification: true,
        redirectTo: '/verify-email',
      },
    };
  } catch (error) {
    return handleApiError(error);
  }
}

export async function loginUser(credentials: LoginCredentials): Promise<ServerAuthResponse> {
  try {
    const email = String(credentials.email ?? '').trim();
    const password = String(credentials.password ?? '');

    if (!email || !password) {
      return {
        status: 'error',
        message: 'Email and password are required',
        errors: [
          { field: 'email', message: 'Email is required', code: 'REQUIRED' },
          { field: 'password', message: 'Password is required', code: 'REQUIRED' },
        ],
      };
    }

    const response = await serverApiClient.post<{
      user?: UserData;
      auth?: AuthTokens;
      permissions?: string[];
      requires_2fa?: boolean;
      requires_verification?: boolean;
      redirectTo?: string;
    }>(
      '/auth/login',
      {
        email: email.toLowerCase(),
        password,
        remember_me: !!credentials.rememberMe,
      },
      { timeout: ENV_CONFIG.API_TIMEOUT }
    );

    const authData = response.data?.auth;
    const userData = response.data?.user;

    if (response.data?.requires_2fa) {
      return {
        status: 'requires_2fa',
        message: 'Two-factor authentication required',
        data: { redirectTo: response.data?.redirectTo || '/2fa' },
      };
    }
    if (response.data?.requires_verification) {
      return {
        status: 'requires_verification',
        message: 'Verification required',
        data: { redirectTo: response.data?.redirectTo || '/verify-email' },
      };
    }

    if (!authData?.access_token || !userData) {
      return {
        status: 'error',
        message: 'Authentication failed - invalid response from server',
        errors: [{ field: 'auth', message: 'Invalid server response', code: 'INVALID_RESPONSE' }],
      };
    }

    const { sessionId, deviceId } = await cookieManager.setAuthCookies({
      userId: userData.id,
      accessToken: authData.access_token,
      refreshToken: authData.refresh_token,
      deviceName: authData.device_name || 'Unknown Device',
      rememberMe: !!credentials.rememberMe,
      expiresIn: authData.access_token_expires_in || SECURITY_CONFIG.ACCESS_TOKEN_MAX_AGE,
      role: userData.user_type as UserRole,
    });

    revalidateTag('user-session', 'layout');
    revalidatePath('/', 'layout');

    const expiresSeconds = authData.access_token_expires_in || SECURITY_CONFIG.ACCESS_TOKEN_MAX_AGE;

    return {
      status: 'success',
      message: 'Login successful',
      data: {
        user: userData,
        auth: authData,
        sessionId,
        deviceId,
        redirectTo: getDashboardPath(userData),
      },
      sessionExpiresAt: Date.now() + expiresSeconds * 1000,
    };
  } catch (error) {
    return handleApiError(error);
  }
}

/* ------------------------------ Email Verify ------------------------------- */

export async function verifyEmail(code: string): Promise<ServerAuthResponse> {
  try {
    const v = String(code ?? '').trim();
    if (!v) {
      return {
        status: 'error',
        message: 'Verification code required',
        errors: [{ field: 'code', message: 'Verification code is required', code: 'REQUIRED' }],
      };
    }

    const response = await serverApiClient.post<any>(
      '/auth/email/verify',
      { code: v },
      { timeout: ENV_CONFIG.API_TIMEOUT }
    );

    return {
      status: 'success',
      message: 'Email verified successfully',
      data: response.data as any,
    };
  } catch (error) {
    return handleApiError(error);
  }
}

export async function resendVerificationEmail(email?: string): Promise<ServerAuthResponse> {
  try {
    const payload = email ? { email: String(email).trim().toLowerCase() } : {};
    const response = await serverApiClient.post<any>(
      '/auth/email/verify/resend',
      payload,
      { timeout: ENV_CONFIG.API_TIMEOUT }
    );

    return {
      status: 'success',
      message: 'Verification email sent',
      data: response.data as any,
    };
  } catch (error) {
    return handleApiError(error);
  }
}

/* --------------------------- Password Management --------------------------- */

export async function requestPasswordReset(email: string): Promise<ServerAuthResponse> {
  try {
    const v = String(email ?? '').trim().toLowerCase();
    if (!validateEmail(v)) {
      return {
        status: 'error',
        message: 'Invalid email address',
        errors: [{ field: 'email', message: 'Invalid email format', code: 'INVALID_EMAIL' }],
      };
    }

    await serverApiClient.post(
      '/auth/password/forgot',
      { email: v },
      { timeout: ENV_CONFIG.API_TIMEOUT }
    );

    return {
      status: 'success',
      message: 'If an account exists with this email, you will receive reset instructions.',
    };
  } catch (error) {
    return handleApiError(error);
  }
}

export async function resetPassword(data: {
  token: string;
  email: string;
  password: string;
  password_confirmation: string;
}): Promise<ServerAuthResponse> {
  try {
    const token = String(data?.token ?? '').trim();
    const email = String(data?.email ?? '').trim().toLowerCase();
    const password = String(data?.password ?? '');
    const password_confirmation = String(data?.password_confirmation ?? '');

    if (!token) {
      return {
        status: 'error',
        message: 'Reset token required',
        errors: [{ field: 'token', message: 'Reset token is required', code: 'REQUIRED' }],
      };
    }
    if (!validateEmail(email)) {
      return {
        status: 'error',
        message: 'Invalid email address',
        errors: [{ field: 'email', message: 'Invalid email format', code: 'INVALID_EMAIL' }],
      };
    }

    const validation = validatePassword(password);
    if (!validation.isValid) {
      return {
        status: 'error',
        message: 'Password validation failed',
        errors: validation.errors.map((err) => ({
          field: 'password',
          message: err,
          code: 'VALIDATION_FAILED',
        })),
      };
    }

    if (password !== password_confirmation) {
      return {
        status: 'error',
        message: 'Passwords do not match',
        errors: [
          {
            field: 'password_confirmation',
            message: 'Passwords must match',
            code: 'PASSWORD_MISMATCH',
          },
        ],
      };
    }

    const response = await serverApiClient.post<any>(
      '/auth/password/reset',
      { token, email, password, password_confirmation },
      { timeout: ENV_CONFIG.API_TIMEOUT }
    );

    return { status: 'success', message: 'Password reset successfully', data: response.data as any };
  } catch (error) {
    return handleApiError(error);
  }
}

export async function changePassword(data: {
  current_password: string;
  new_password: string;
  new_password_confirmation: string;
}): Promise<ServerAuthResponse> {
  try {
    const current_password = String(data?.current_password ?? '');
    const new_password = String(data?.new_password ?? '');
    const new_password_confirmation = String(data?.new_password_confirmation ?? '');

    if (!current_password) {
      return {
        status: 'error',
        message: 'Current password required',
        errors: [{ field: 'current_password', message: 'Current password is required', code: 'REQUIRED' }],
      };
    }

    const validation = validatePassword(new_password);
    if (!validation.isValid) {
      return {
        status: 'error',
        message: 'Password validation failed',
        errors: validation.errors.map((err) => ({
          field: 'new_password',
          message: err,
          code: 'VALIDATION_FAILED',
        })),
      };
    }

    if (new_password !== new_password_confirmation) {
      return {
        status: 'error',
        message: 'Passwords do not match',
        errors: [
          {
            field: 'new_password_confirmation',
            message: 'Passwords must match',
            code: 'PASSWORD_MISMATCH',
          },
        ],
      };
    }

    const response = await serverApiClient.post<any>(
      '/auth/password/change',
      { current_password, new_password, new_password_confirmation },
      { timeout: ENV_CONFIG.API_TIMEOUT }
    );

    return { status: 'success', message: 'Password changed successfully', data: response.data as any };
  } catch (error) {
    return handleApiError(error);
  }
}

/* --------------------------- Availability / Checks -------------------------- */

export async function checkEmailAvailability(email: string): Promise<ServerAuthResponse> {
  try {
    const v = String(email ?? '').trim().toLowerCase();
    if (!validateEmail(v)) {
      return {
        status: 'error',
        message: 'Invalid email address',
        errors: [{ field: 'email', message: 'Invalid email format', code: 'INVALID_EMAIL' }],
      };
    }

    const response = await serverApiClient.post<any>(
      '/auth/check-email',
      { email: v },
      { timeout: ENV_CONFIG.API_TIMEOUT }
    );

    return { status: 'success', message: 'Email availability checked', data: response.data as any };
  } catch (error) {
    return handleApiError(error);
  }
}

/* --------------------------- Session Management ---------------------------- */

export async function logoutUser(): Promise<ServerAuthResponse> {
  try {
    const c = (await cookieManager.getAuthCookies()) as AuthCookies;
    const accessToken = c.access_token;

    if (accessToken) {
      try {
        await serverApiClient.post(
          '/auth/logout',
          {},
          { headers: { Authorization: `Bearer ${accessToken}` }, timeout: ENV_CONFIG.API_TIMEOUT }
        );
      } catch (apiError) {
        console.warn('Logout API call failed:', apiError);
      }
    }

    await cookieManager.clearAuthCookies({ preserveDevice: false });

    revalidateTag('user-session', 'layout');
    revalidatePath('/', 'layout');

    return {
      status: 'success',
      message: 'Logged out successfully',
      data: { redirectTo: '/login?message=logged_out' },
    };
  } catch (error) {
    console.error('Logout error:', error);
    await cookieManager.clearAuthCookies();
    return {
      status: 'error',
      message: 'Logout completed with errors',
      data: { redirectTo: '/login' },
    };
  }
}

export async function logoutAllSessions(): Promise<ServerAuthResponse> {
  try {
    const c = (await cookieManager.getAuthCookies()) as AuthCookies;
    const accessToken = c.access_token;

    if (!accessToken) {
      return {
        status: 'unauthenticated',
        message: 'Not authenticated',
        errors: [{ field: 'auth', message: 'Authentication required', code: 'UNAUTHORIZED' }],
      };
    }

    await serverApiClient.post(
      '/auth/logout-all',
      {},
      { headers: { Authorization: `Bearer ${accessToken}` }, timeout: ENV_CONFIG.API_TIMEOUT }
    );

    await cookieManager.clearAuthCookies({ preserveDevice: false });

    revalidateTag('user-session', 'layout');
    revalidatePath('/', 'layout');

    return {
      status: 'success',
      message: 'Logged out from all devices',
      data: { redirectTo: '/login?message=logged_out_all' },
    };
  } catch (error) {
    return handleApiError(error);
  }
}

export async function getActiveSessions(): Promise<ServerAuthResponse> {
  try {
    const response = await serverApiClient.get<{ sessions?: SessionMetadata[] }>(
      '/auth/sessions',
      { timeout: ENV_CONFIG.API_TIMEOUT }
    );

    return {
      status: 'success',
      message: 'Sessions fetched successfully',
      data: { sessions: response.data?.sessions || [] },
    };
  } catch (error) {
    return handleApiError(error);
  }
}

/* ------------------------------- Current User ------------------------------ */

export async function getCurrentUser(): Promise<ServerAuthResponse> {
  try {
    const response = await getCurrentUserCoalesced();
    if (!response.data?.user) {
      return { status: 'unauthenticated', message: 'Not authenticated' };
    }

    return {
      status: 'success',
      message: 'User fetched successfully',
      data: {
        user: response.data.user,
        permissions: response.data.permissions || [],
      },
    };
  } catch (error) {
    const handled = handleApiError(error);
    if (handled.status === 'unauthenticated') {
      return { status: 'unauthenticated', message: handled.message || 'Session expired' };
    }
    return handled;
  }
}

/* ----------------------------- Profile Management -------------------------- */

export async function updateProfile(data: Partial<any>): Promise<ServerAuthResponse> {
  try {
    const response = await serverApiClient.put<any>(
      '/auth/profile',
      data,
      { timeout: ENV_CONFIG.API_TIMEOUT }
    );

    revalidateTag('user-profile', 'layout');

    return {
      status: 'success',
      message: 'Profile updated successfully',
      data: { user: response.data?.user },
    };
  } catch (error) {
    return handleApiError(error);
  }
}

/* =============================================================================
 * UTILITY FUNCTIONS FOR CLIENT-SIDE USE
 * =============================================================================
 */

// Helper to safely refresh without logout on transient errors
export async function safeRefreshSession(): Promise<{
  success: boolean;
  shouldRetry: boolean;
  error?: string;
}> {
  try {
    const result = await refreshIfNeeded();
    
    if (result.ok) {
      return { success: true, shouldRetry: false };
    }
    
    if (result.transient) {
      return {
        success: false,
        shouldRetry: true,
        error: result.error?.message || 'Temporary network issue',
      };
    }
    
    if (result.logoutRequired) {
      return {
        success: false,
        shouldRetry: false,
        error: result.error?.message || 'Session expired',
      };
    }
    
    return {
      success: false,
      shouldRetry: false,
      error: result.error?.message || 'Refresh failed',
    };
  } catch (error) {
    console.error('Unexpected error in safeRefreshSession:', error);
    return {
      success: false,
      shouldRetry: true,
      error: 'Unexpected error during refresh',
    };
  }
}

// Check if session is valid without triggering refresh
export async function checkSessionHealth(): Promise<{
  healthy: boolean;
  needsRefresh: boolean;
  error?: string;
}> {
  try {
    const session = await validateSession();
    
    if (session.status === 'AUTHENTICATED') {
      return { healthy: true, needsRefresh: false };
    }
    
    if (session.status === 'UNAUTHENTICATED') {
      // Check if we have cookies that might allow refresh
      const cookies = (await cookieManager.getAuthCookies()) as AuthCookies;
      const hasRefreshToken = !!cookies.refresh_token;
      const hasSessionId = !!cookies.session_id;
      
      if (hasRefreshToken && hasSessionId) {
        return { healthy: false, needsRefresh: true, error: session.error?.message };
      }
    }
    
    return { 
      healthy: false, 
      needsRefresh: false, 
      error: session.status === 'UNKNOWN' 
        ? 'Service temporarily unavailable' 
        : session.error?.message 
    };
  } catch (error) {
    console.error('Error checking session health:', error);
    return {
      healthy: false,
      needsRefresh: false,
      error: 'Unable to check session status',
    };
  }
}

// ✅ NEW: Helper for layouts to safely validate session without redirects
export async function safeValidateSession(): Promise<{
  status: 'authenticated' | 'unauthenticated' | 'unknown';
  user?: UserData;
  shouldShowLoader?: boolean;
}> {
  const session = await validateSession();
  
  switch (session.status) {
    case 'AUTHENTICATED':
      return { 
        status: 'authenticated', 
        user: session.user,
        shouldShowLoader: false 
      };
      
    case 'UNAUTHENTICATED':
      return { 
        status: 'unauthenticated', 
        shouldShowLoader: false 
      };
      
    case 'UNKNOWN':
      // ✅ FIX: UNKNOWN status means show loader, not redirect
      return { 
        status: 'unknown', 
        shouldShowLoader: true 
      };
      
    default:
      return { 
        status: 'unknown', 
        shouldShowLoader: true 
      };
  }
}

/* =============================================================================
 * INIT CHECKS (SAFE)
 * =============================================================================
 */

if (process.env.NODE_ENV === 'production') {
  try {
    const isValid = encryptionService.validateKey();
    if (!isValid && process.env.COOKIE_ENCRYPTION_KEY) {
       console.error('Invalid encryption key configuration');
    }
  } catch (error) {
    console.error('Encryption service initialization failed:', error);
  }
}